import { Reducer } from 'redux';
import { ActionCreator, ActionType, TypeConstant } from './reduxUtils';

export interface ActionHandler<TState, TAction> {
  (state: TState, action: TAction): TState;
}

export class ReducerBuilder<TState, TAction extends { type: string }> {
  private handlers: Map<string, ActionHandler<TState, TAction>> = new Map();

  constructor(private defaultState: TState) {}

  handleAction<T extends ActionCreator<any, any>>(
    actionOrActions: T | T[],
    handler: ActionHandler<TState, ActionType<T>>
  ): this {
    const types = this.extractTypes(actionOrActions);
    types.forEach(type => {
      this.handlers.set(type, handler as ActionHandler<TState, TAction>);
    });
    return this;
  }

  private extractTypes(actionOrActions: ActionCreator | ActionCreator[]): TypeConstant[] {
    if (Array.isArray(actionOrActions)) {
      return actionOrActions.map(a => a.type);
    }
    return [actionOrActions.type];
  }

  build(): Reducer<TState, TAction> {
    return (state = this.defaultState, action) => {
      const handler = this.handlers.get(action.type);
      return handler ? handler(state, action) : state;
    };
  }
}

export const createReducer = <TState, TAction extends { type: string }>(defaultState: TState) =>
  new ReducerBuilder<TState, TAction>(defaultState);

export type StateType<TReducerOrMap extends any> = TReducerOrMap extends Reducer<any, any>
  ? ReturnType<TReducerOrMap>
  : TReducerOrMap extends Record<any, any>
  ? {
      [K in keyof TReducerOrMap]: StateType<TReducerOrMap[K]>;
    }
  : never;
